/** feat-338 — small shared render pieces for Question Analysis. */
import React, { createContext, useContext } from 'react';
import { AlertTriangle } from 'lucide-react';
import { C, DEFAULT_SUPPRESS_BELOW_LEARNERS, QA_COPY } from './questionAnalysisFormat';

/**
 * THE one source for the learner floor on screen. The page provides the value from the
 * most recent `/questions` `meta.thresholds.suppress_below_learners`; before any has
 * loaded (or for a `/time`-only render) it is DEFAULT_SUPPRESS_BELOW_LEARNERS.
 */
export const SuppressFloorContext = createContext<number>(DEFAULT_SUPPRESS_BELOW_LEARNERS);
export const useSuppressFloor = () => useContext(SuppressFloorContext);

/** A suppressed cell: "—" (labelled for screen readers) + the visible hint. Never a number. */
export const SuppressedValue: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
  const floor = useSuppressFloor();
  return (
    <span data-testid="suppressed">
      <span className={C.dash} aria-label={QA_COPY.hiddenLabel(floor)}>
        —
      </span>
      <span className={inline ? 'inline ml-2 text-[11px] text-inkMuted' : C.suppressHint}>{QA_COPY.fewerThan(floor)}</span>
    </span>
  );
};

/**
 * A null metric: muted dash with an explanatory title. Without a specific title it says
 * "No data, or fewer than {floor} learners" (floor from the single source): under r3 the
 * API can't tell "no rows" from "below the learner floor".
 */
export const NullDash: React.FC<{ title?: string }> = ({ title }) => {
  const floor = useSuppressFloor();
  return (
    <span className={C.dash} title={title ?? QA_COPY.noValue(floor)} data-testid="null-dash">
      —
    </span>
  );
};

/**
 * A count the API withheld (r3: null when fewer than `floor` learners). Rendered as
 * "fewer than {floor}", never 0 and never blank.
 */
export const FewerThan: React.FC<{ floor: number; suffix?: string }> = ({ floor, suffix = '' }) => (
  <span className="text-inkMuted" data-testid="fewer-than" title={`Hidden: fewer than ${floor} learners`}>
    {`fewer than ${floor}${suffix}`}
  </span>
);

/** A learner count, or "fewer than {floor}" when the API withheld it. */
export function countOrFewer(v: number | null, floor: number, suffix = ''): React.ReactNode {
  return v == null ? <FewerThan floor={floor} suffix={suffix} /> : `${v}${suffix}`;
}

/** Value or dash. */
export function orDash(v: string | number | null | undefined, title?: string): React.ReactNode {
  return v == null ? <NullDash title={title} /> : v;
}

export const ErrorBanner: React.FC<{
  title: string;
  detail: string;
  onRetry?: () => void;
  busy?: boolean;
  testId?: string;
  retryLabel?: string;
  className?: string;
}> = ({ title, detail, onRetry, busy, testId, retryLabel = QA_COPY.tryAgain, className = '' }) => (
  <div role="alert" data-testid={testId} className={`${C.banner} ${className}`}>
    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-danger" aria-hidden="true" />
    <div className="flex-1 min-w-0">
      <p className="font-bold">{title}</p>
      <p className="text-xs mt-0.5">{detail}</p>
    </div>
    {onRetry && (
      <button type="button" onClick={onRetry} disabled={busy} className={C.bannerBtn}>
        {retryLabel}
      </button>
    )}
  </div>
);

export function httpStatusOf(err: unknown): number | null {
  const s = (err as { status?: unknown } | null)?.status;
  return typeof s === 'number' ? s : null;
}
